<script setup lang="ts">
import { mdiChevronRight, mdiImageOutline } from '@mdi/js'

// #719 観点B/D: モバイルの横型リストカード（左40%=画像 / 右60%=テキスト、背高め）。
// 魔法研究会（作品）/ ゆりかご書庫（魔導書）/ ゆりかご結晶（作品）/ Emporium（商品）で共有。
// カード全体がタップ領域（入れ子ボタンを避けるため内部に <button> は置かない）。
const props = withDefaults(defineProps<{
  image?: string
  /**
   * #763: 配信用 animated WebP。あればこちらを先に出し、非対応環境は `image`
   * （GIF 正本）へ落ちる。実測で GIF の 39%（61% 減）なので、1 ページに 20 枚
   * 読むモバイル一覧では体感に直結する。
   */
  imageWebp?: string
  title: string
  subtitle?: string
  /** 画像の収め方。作品サムネ=cover、本の表紙=contain。 */
  imageFit?: 'cover' | 'contain'
}>(), {
  imageFit: 'cover',
})

const emit = defineEmits<{ click: [] }>()

// 画像が無い / 読み込み失敗のときは真っ黒でなく、テーマ地＋アイコンのプレースホルダを出す。
const imgError = ref(false)
watch(() => props.image, () => {
  imgError.value = false
})
const showImage = computed(() => !!props.image && !imgError.value)
</script>

<template>
  <div
    class="mlc"
    role="button"
    tabindex="0"
    @click="emit('click')"
    @keydown.enter="emit('click')"
    @keydown.space.prevent="emit('click')"
  >
    <div class="mlc__img">
      <picture v-if="showImage">
        <source
          v-if="imageWebp"
          :srcset="imageWebp"
          type="image/webp"
        >
        <img
          :src="image"
          :alt="title"
          loading="lazy"
          class="mlc__img-el"
          :class="{ 'mlc__img-el--contain': imageFit === 'contain' }"
          @error="imgError = true"
        >
      </picture>
      <div
        v-else
        class="mlc__img-placeholder"
        aria-hidden="true"
      >
        <v-icon
          :icon="mdiImageOutline"
          size="30"
        />
      </div>
      <!-- サムネ右上のオーバーレイ（#752: お気に入り★等）。opt-in の名前付き
           スロットで、未使用時は何も描画しない（共有コンポーネントなので非侵襲）。 -->
      <div
        v-if="$slots.imageOverlay"
        class="mlc__img-overlay"
      >
        <slot name="imageOverlay" />
      </div>
    </div>

    <div class="mlc__body">
      <div class="mlc__title">
        {{ title }}
      </div>
      <div
        v-if="subtitle"
        class="mlc__subtitle"
      >
        {{ subtitle }}
      </div>
      <div class="mlc__meta">
        <slot name="meta" />
      </div>
    </div>

    <v-icon
      :icon="mdiChevronRight"
      class="mlc__chevron"
    />
  </div>
</template>

<style scoped>
.mlc {
  width: 100%;
  min-height: 128px;
  display: flex;
  align-items: stretch;
  gap: 0;
  border: 1px solid rgba(128, 0, 32, 0.22);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.08s ease, box-shadow 0.08s ease;
  font-family: "Noto Sans JP", sans-serif;
}

.mlc:active {
  transform: scale(0.99);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.22);
}

.mlc__img {
  position: relative;
  flex: 0 0 40%;
  max-width: 40%;
  background: linear-gradient(135deg, #6b5844, #3a2f22);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* サムネ右上オーバーレイ（#752）。画像に被さるので pointer-events は子（★）だけ有効化。 */
.mlc__img-overlay {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1;
  display: flex;
  pointer-events: none;
}

.mlc__img-overlay > :deep(*) {
  pointer-events: auto;
}

.mlc__img-placeholder {
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
}

.mlc__img-el {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.mlc__img-el--contain {
  object-fit: contain;
  padding: 6px;
}

.mlc__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 4px 12px 12px;
}

.mlc__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #3a2417;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.3;
}

.mlc__subtitle {
  font-size: 0.82rem;
  color: #7a6a58;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.35;
}

.mlc__meta {
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 0;
}

.mlc__chevron {
  flex: 0 0 auto;
  align-self: center;
  color: #a08b6a;
  margin-right: 4px;
}
</style>
